import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState, TaskStatusBadge, OPP_TYPE_LABELS } from "@/components/shared";
import { ListChecks } from "lucide-react";
import { toast } from "sonner";

export default function AITasks() {
  const { activeWebsite } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let timer;
    const load = async () => {
      if (!activeWebsite) { setLoading(false); return; }
      try {
        const { data: d } = await api.get(`/websites/${activeWebsite.id}/tasks`);
        setData(d);
      } catch (e) { toast.error(errMsg(e)); }
      setLoading(false);
    };
    setLoading(true);
    load();
    timer = setInterval(load, 8000);
    return () => clearInterval(timer);
  }, [activeWebsite]);

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold tracking-tight">AI Tasks</h1>
        <p className="text-xs text-muted-foreground">Every task is human-approved, scoped and validated. Status updates live.</p>
      </div>
      {!data || data.total === 0 ? (
        <EmptyState icon={ListChecks} title="No AI tasks yet"
          message="Approve an opportunity (Approve & Run) to create your first scoped AI task." testId="tasks-empty">
          <Link to="/dashboard/opportunities" className="text-sm font-medium text-primary" data-testid="tasks-goto-opportunities">Browse opportunities</Link>
        </EmptyState>
      ) : (
        <Card className="shadow-none">
          <CardContent className="scc-table-wrap p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden md:table-cell">Approved by</TableHead>
                  <TableHead className="hidden md:table-cell">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((t) => (
                  <TableRow key={t.id} data-testid="task-row">
                    <TableCell>
                      <Link to={`/dashboard/tasks/${t.id}`} className="text-xs font-medium text-primary hover:underline" data-testid="task-link">{t.title}</Link>
                    </TableCell>
                    <TableCell className="text-xs">{OPP_TYPE_LABELS[t.type] || t.type}</TableCell>
                    <TableCell><TaskStatusBadge status={t.status} /></TableCell>
                    <TableCell className="hidden text-xs text-muted-foreground md:table-cell">{t.approvedBy}</TableCell>
                    <TableCell className="hidden text-xs text-muted-foreground md:table-cell">{new Date(t.created_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
